import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { message } from "antd";
import Utilities from "../../dependencies/custom/react-utilities";
import domainSettings from "../../dependencies/custom/settings";

export const useInstructionalMaterials = () => {
  const navigate = useNavigate();
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchMaterials = useCallback(
    async (params = {}) => {
      setLoading(true);
      try {
        const queryObj = {};
        if (params.acadyrID) queryObj.acadyrID = params.acadyrID;
        if (params.termID) queryObj.termID = params.termID;
        if (params.type) queryObj.type = params.type;
        if (params.status) queryObj.status = params.status;
        if (params.subjectID) queryObj.subjectID = params.subjectID;

        const queryParams = new URLSearchParams(queryObj).toString();
        const url = `${domainSettings.urls.academics.instructionalMaterials}${queryParams ? "?" + queryParams : ""}`;
        const response = await Utilities.fetchWithAuth(
          "GET",
          url,
          null,
          navigate,
        );

        if (response.ok) {
          setMaterials(
            Array.isArray(response.data)
              ? response.data
              : response.data?.results || [],
          );
        } else {
          Utilities.showError(response, "Failed to fetch materials");
        }
      } catch (error) {
        message.error("Error fetching materials");
        console.error(error);
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  const fetchMaterialDetail = useCallback(
    async (id) => {
      try {
        const url =
          domainSettings.urls.academics.instructionalMaterialDetail.replace(
            ":id",
            id,
          );
        const response = await Utilities.fetchWithAuth(
          "GET",
          url,
          null,
          navigate,
        );
        if (response.ok) return response.data;
        Utilities.showError(response, "Failed to fetch material details");
        return null;
      } catch (error) {
        message.error("Error fetching material details");
        console.error(error);
        return null;
      }
    },
    [navigate],
  );

  const createMaterial = useCallback(
    async (data) => {
      setLoading(true);
      try {
        const response = await Utilities.fetchWithAuth(
          "POST",
          domainSettings.urls.academics.instructionalMaterials,
          data,
          navigate,
        );
        if (response.ok) {
          message.success("Material created successfully");
          return response.data;
        }
        Utilities.showError(response, "Failed to create material");
        return null;
      } catch (error) {
        message.error("Error creating material");
        console.error(error);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  const updateMaterial = useCallback(
    async (id, data) => {
      setLoading(true);
      try {
        const url =
          domainSettings.urls.academics.instructionalMaterialDetail.replace(
            ":id",
            id,
          );
        const response = await Utilities.fetchWithAuth(
          "PATCH",
          url,
          data,
          navigate,
        );
        if (response.ok) {
          message.success("Material updated successfully");
          return response.data;
        }
        Utilities.showError(response, "Failed to update material");
        return null;
      } catch (error) {
        message.error("Error updating material");
        console.error(error);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  const deleteMaterial = useCallback(
    async (id) => {
      try {
        const url =
          domainSettings.urls.academics.instructionalMaterialDetail.replace(
            ":id",
            id,
          );
        const response = await Utilities.fetchWithAuth(
          "DELETE",
          url,
          null,
          navigate,
        );
        if (response.ok) {
          message.success("Material deleted successfully");
          return true;
        }
        Utilities.showError(response, "Failed to delete material");
        return false;
      } catch (error) {
        message.error("Error deleting material");
        console.error(error);
        return false;
      }
    },
    [navigate],
  );

  const archiveMaterial = useCallback(
    async (id) => {
      try {
        const url =
          domainSettings.urls.academics.instructionalMaterialDetail.replace(
            ":id",
            id,
          );
        const response = await Utilities.fetchWithAuth(
          "PATCH",
          url,
          { status: "Archived" },
          navigate,
        );
        if (response.ok) {
          message.success("Material archived successfully");
          return true;
        }
        Utilities.showError(response, "Failed to archive material");
        return false;
      } catch (error) {
        message.error("Error archiving material");
        console.error(error);
        return false;
      }
    },
    [navigate],
  );

  const uploadMaterialFiles = useCallback(
    async (id, files) => {
      try {
        const formData = new FormData();
        files.forEach((f) => formData.append("files", f));
        const url =
          domainSettings.urls.academics.instructionalMaterialFiles.replace(
            ":id",
            id,
          );
        const response = await Utilities.fetchWithAuth(
          "POST",
          url,
          formData,
          navigate,
        );
        if (response.ok) {
          message.success("Files uploaded successfully");
          return response.data;
        }
        Utilities.showError(response, "Failed to upload files");
        return null;
      } catch (error) {
        message.error("Error uploading files");
        console.error(error);
        return null;
      }
    },
    [navigate],
  );

  const deleteMaterialFile = useCallback(
    async (materialId, fileId) => {
      try {
        const url =
          domainSettings.urls.academics.instructionalMaterialFileDelete
            .replace(":id", materialId)
            .replace(":fileId", fileId);
        const response = await Utilities.fetchWithAuth(
          "DELETE",
          url,
          null,
          navigate,
        );
        if (response.ok) {
          message.success("File removed successfully");
          return true;
        }
        Utilities.showError(response, "Failed to remove file");
        return false;
      } catch (error) {
        message.error("Error removing file");
        console.error(error);
        return false;
      }
    },
    [navigate],
  );

  const submitMaterial = useCallback(
    async (id) => {
      try {
        const url =
          domainSettings.urls.academics.instructionalMaterialSubmit.replace(
            ":id",
            id,
          );
        const response = await Utilities.fetchWithAuth(
          "POST",
          url,
          null,
          navigate,
        );
        if (response.ok) {
          message.success("Material submitted for approval");
          return true;
        }
        Utilities.showError(response, "Failed to submit material");
        return false;
      } catch (error) {
        message.error("Error submitting material");
        console.error(error);
        return false;
      }
    },
    [navigate],
  );

  const confirmDelivery = useCallback(
    async (id) => {
      try {
        const url =
          domainSettings.urls.academics.instructionalMaterialConfirmDelivery.replace(
            ":id",
            id,
          );
        const response = await Utilities.fetchWithAuth(
          "POST",
          url,
          null,
          navigate,
        );
        if (response.ok) {
          message.success("Delivery confirmed successfully");
          return response.data || true;
        }
        Utilities.showError(response, "Failed to confirm delivery");
        return null;
      } catch (error) {
        message.error("Error confirming delivery");
        console.error(error);
        return null;
      }
    },
    [navigate],
  );

  const reverseDelivery = useCallback(
    async (id) => {
      try {
        const url =
          domainSettings.urls.academics.instructionalMaterialReverseDelivery.replace(
            ":id",
            id,
          );
        const response = await Utilities.fetchWithAuth(
          "POST",
          url,
          null,
          navigate,
        );
        if (response.ok) {
          message.success("Delivery mark removed successfully");
          return response.data || true;
        }
        Utilities.showError(response, "Failed to reverse delivery");
        return null;
      } catch (error) {
        message.error("Error reversing delivery");
        console.error(error);
        return null;
      }
    },
    [navigate],
  );

  return {
    materials,
    loading,
    fetchMaterials,
    fetchMaterialDetail,
    createMaterial,
    updateMaterial,
    deleteMaterial,
    archiveMaterial,
    uploadMaterialFiles,
    deleteMaterialFile,
    submitMaterial,
    confirmDelivery,
    reverseDelivery,
  };
};

export default useInstructionalMaterials;
